export const normalize = (value) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
export const linkName = (link) => link?.name || `M3U #${link?.id ?? '—'}`;
// Catalog dates can be missing or contain legacy values that Date cannot parse.
function parseDate(value) {
  if (!value || !(typeof value === 'string' || typeof value === 'number' || value instanceof Date))
    return null;
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date : null;
}

export const timestamp = (value) => parseDate(value)?.getTime() ?? 0;
export const isExpired = (value, now = Date.now()) => timestamp(value) <= now;
export const isSoon = (value, now = Date.now()) =>
  !isExpired(value, now) && timestamp(value) <= now + 30 * 86400000;
export const formatDate = (value) => {
  if (!value) return 'Sin fecha';
  const date = parseDate(value);
  return date
    ? new Intl.DateTimeFormat('es-ES', {dateStyle: 'medium', timeStyle: 'short'}).format(date)
    : 'Fecha no válida';
};
export const daysLabel = (value, now = Date.now()) => {
  if (!value) return 'Sin fecha';
  const date = parseDate(value);
  if (!date) return 'Fecha no válida';
  const days = Math.ceil((date.getTime() - now) / 86400000);
  if (days <= 0) return 'Caducado';
  return days === 1 ? 'En menos de 24 h' : `En ${days} días`;
};

export function toDateInput(value) {
  const date = parseDate(value);
  if (!date) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return Number.isFinite(local.getTime()) ? local.toISOString().slice(0, 16) : '';
}

export const fromDateInput = (value) => (value ? new Date(value).toISOString() : null);

export function providerHost(path) {
  try {
    return new URL(path).hostname;
  } catch {
    return 'Archivo local';
  }
}

export function userStatus(user, links, now = Date.now()) {
  if (!user.enabled) return {key: 'disabled', label: 'Desactivado', color: 'default'};
  const link = links.find((item) => item.id === user.m3uId);
  if (!link) return {key: 'unassigned', label: 'Sin M3U', color: 'warning'};
  if (!link.enabled) return {key: 'unavailable', label: 'M3U desactivado', color: 'error'};
  if (isExpired(link.expiresAt, now))
    return {key: 'unavailable', label: 'M3U caducado', color: 'error'};
  if (!user.m3uExpiresAt) return {key: 'expired', label: 'Sin caducidad', color: 'warning'};
  if (isExpired(user.m3uExpiresAt, now))
    return {key: 'expired', label: 'Suscripción caducada', color: 'error'};
  if (user.maxActiveStreams === 0)
    return {key: 'unavailable', label: 'Sin pantallas', color: 'warning'};
  return {key: 'active', label: 'Activo', color: 'success'};
}

export function filterLinks(links, filters, now = Date.now()) {
  return links
    .filter((link) => {
      const query = normalize(filters.q).trim();
      return (
        (!query ||
          [link.name, link.id, link.path, link.notes].some((v) => normalize(v).includes(query))) &&
        (filters.expiry === 'all' ||
          (filters.expiry === 'expired'
            ? isExpired(link.expiresAt, now)
            : filters.expiry === 'soon'
            ? isSoon(link.expiresAt, now)
            : !isExpired(link.expiresAt, now))) &&
        (filters.state === 'all' || link.enabled === (filters.state === 'enabled')) &&
        (filters.capacity === 'all' ||
          (filters.capacity === 'available'
            ? link.assignedScreens < link.maxClients
            : filters.capacity === 'over'
            ? link.assignedScreens > link.maxClients
            : link.assignedScreens >= link.maxClients))
      );
    })
    .sort((a, b) => {
      if (filters.sort === 'name')
        return linkName(a).localeCompare(linkName(b), 'es') || a.id - b.id;
      if (filters.sort === 'capacity')
        return b.maxClients - b.assignedScreens - (a.maxClients - a.assignedScreens) || a.id - b.id;
      if (filters.sort === 'assigned') return b.assignedScreens - a.assignedScreens || a.id - b.id;
      return timestamp(a.expiresAt) - timestamp(b.expiresAt) || a.id - b.id;
    });
}

export function filterUsers(users, links, filters, now = Date.now()) {
  return users
    .filter((user) => {
      const query = normalize(filters.q).trim();
      const status = userStatus(user, links, now).key;
      const link = links.find((item) => item.id === user.m3uId);
      return (
        (!query ||
          [user.name, user.email, user.phone, user.publicPath, link?.name].some((v) =>
            normalize(v).includes(query)
          )) &&
        (filters.state === 'all' || filters.state === status) &&
        (filters.m3u === 'all' ||
          (filters.m3u === 'none' ? user.m3uId === null : user.m3uId === Number(filters.m3u))) &&
        (filters.expiry === 'all' ||
          (filters.expiry === 'soon'
            ? isSoon(user.m3uExpiresAt, now)
            : isExpired(user.m3uExpiresAt, now))) &&
        (filters.variant === 'all' || user.playerVariant === filters.variant)
      );
    })
    .sort((a, b) => {
      if (filters.sort === 'expiry')
        return (
          (timestamp(a.m3uExpiresAt) || Infinity) - (timestamp(b.m3uExpiresAt) || Infinity) ||
          a.id - b.id
        );
      if (filters.sort === 'screens') return b.maxActiveStreams - a.maxActiveStreams || a.id - b.id;
      return (a.name || a.email).localeCompare(b.name || b.email, 'es') || a.id - b.id;
    });
}

export function projectedScreens(link, user, maxScreens) {
  return (
    (link?.assignedScreens || 0) -
    (user?.m3uId === link?.id ? user.maxActiveStreams : 0) +
    Number(maxScreens || 0)
  );
}

// Spreadsheet formula prefixes must stay text when opened by an administrator.
export function csvCell(value) {
  let text = String(value ?? '');
  if (/^[\s]*[=+\-@]/.test(text)) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
}

export function downloadCsv(filename, rows) {
  const blob = new Blob(['\uFEFF' + rows.map((row) => row.map(csvCell).join(';')).join('\r\n')], {
    type: 'text/csv;charset=utf-8;'
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
